import React, { useEffect, useState, useRef } from 'react'
import { BaseLayout } from 'src/layouts'
import styles from './styles.module.scss'
import { CollectionPage } from 'schema-dts'
import { useAppIntl } from 'src/i18n'
//Services
import { getReviewIndex } from 'src/services/reviewServices'
//Imported States
import { useListIndex, useListReadAlso } from 'src/state'
import { WEBSITE } from 'src/consts'
//import { useAllReviews } from 'src/state'
//Imported components
import HowWeRate from './components/HowWeRate'
import { ListDetailsHeader } from './components/Header'
import TopList from './components/TopList'
import { ContentInfo } from './components/ContentInfo'
import { RatingChart } from './components/RatingChart'
import { ReadAlso } from './components/ReadAlso'
import { StickyContentNav } from 'src/components/StickyContentNav'

export type ListDetailsProps = {
  slug: string
}

export const ListDetails = ({ slug }: ListDetailsProps) => {
  const {
    title,
    shortPreview,
    mainImage,
    upperContent,
    lowerContent,
    reviews,
    author,
    updatedAt,
  } = useListIndex({ slug }).data
  const intl = useAppIntl()
  const readAlsoData = useListReadAlso().data
  const reviewSlugs = reviews.data.map(item => item.attributes.slug) // This is an array of slugs for each review
  const currentListSlug = useListIndex({ slug }).data.slug
  const [detailedReviewItems, setDetailedReviewItems] = useState([])
  const howWeRateRef = useRef(null)
  const [reviewLinks, setReviewLinks] = useState([])
  useEffect(() => {
    const links = detailedReviewItems.map(review => ({
      title: review.title,
      targetId: review.slug,
    }))
    links.push({
      title: intl.t('tableOfContent.title1'),
      targetId: 'lowerContent',
    })

    links.push({
      title: intl.t('tableOfContent.title2'),
      targetId: 'howWeRate',
    })

    setReviewLinks(links)
  }, [detailedReviewItems, intl])

  useEffect(() => {
    if (!!reviewSlugs && reviewSlugs.length > 0) {
      const fetchPromises = reviewSlugs.map(slug => getReviewIndex({ slug }))

      Promise.all(fetchPromises)
        .then(fetchedItems => {
          const filteredReviews = fetchedItems.reduce((acc, item) => {
            const matchingRatings = item?.ratings.data?.filter(rating => {
              const hasMatchingListSlug =
                rating.attributes?.list?.data?.attributes?.slug ===
                currentListSlug
              return hasMatchingListSlug
            })

            if (!!matchingRatings && matchingRatings.length > 0) {
              acc.push({ ...item, ratings: { data: matchingRatings } })
            }

            return acc
          }, [])

          setDetailedReviewItems(filteredReviews)
        })
        .catch(error => {
          console.error('Error fetching detailed list items:', error)
        })
    }
  }, [])

  const getGeneralRatingScore = ratingsData => {
    const generalRating = ratingsData.find(rating => {
      return (
        rating.attributes.ratingAttribute.data.attributes.name === 'Overall'
      )
    })

    return generalRating ? generalRating.attributes.score : null
  }

  return (
    <main className={styles.container}>
      <ListDetailsHeader
        title={title}
        shortPreview={shortPreview}
        author={author}
        mainImage={mainImage}
        upperContent={upperContent}
        updatedAt={updatedAt}
      />
      <section className={styles.padding}>
        <div className={styles.main}>
          <TopList
            detailedReviewItems={detailedReviewItems}
            getGeneralRatingScore={getGeneralRatingScore}
          />
        </div>
      </section>
      <section className={styles.ratingChart}>
        <RatingChart
          detailedReviewItems={detailedReviewItems}
          scrollToHowWeRate={() =>
            howWeRateRef.current.scrollIntoView({ behavior: 'smooth' })
          }
        />
      </section>
      <section className={styles.ratingReviews}>
        <div>
          <ContentInfo
            lowerContent={lowerContent}
            detailedReviewItems={detailedReviewItems}
            getGeneralRatingScore={getGeneralRatingScore}
          />
          <HowWeRate
            detailedReviewItems={detailedReviewItems}
            ref={howWeRateRef}
          />
        </div>
        <aside className={styles.stickyNav}>
          <StickyContentNav links={reviewLinks} />
        </aside>
      </section>
      {readAlsoData && (
        <div className={styles.readAlso}>
          <ReadAlso data={readAlsoData} />
        </div>
      )}
    </main>
  )
}

const ListDetailsLayout = ({ children, slug }) => {
  const { seo, author, title, reviews } = useListIndex({ slug }).data
  const reviewSlugs = reviews.data.map(item => item.attributes.slug)
  const [detailedReviewItems, setDetailedReviewItems] = useState([])

  useEffect(() => {
    if (reviewSlugs.length > 0) {
      // Map each slug to a fetch promise
      const fetchPromises = reviewSlugs.map(slug => getReviewIndex({ slug }))

      Promise.all(fetchPromises)
        .then(fetchedItems => {
          setDetailedReviewItems(fetchedItems)
        })
        .catch(error => {
          console.error('Error fetching detailed list items:', error)
        })
    }
  }, [])
  function stripHtml(html) {
    if (html === null || html === undefined) {
      return ''
    }
    let text = html.replace(/<[^>]*>/g, '') // Remove HTML tags
    text = text.replace(/&nbsp;/gi, ' ') // Replace &nbsp; with a regular space
    return text
  }
  const reviewListStructuredData: CollectionPage = {
    '@type': 'CollectionPage',
    name: seo?.metaTitle,
    description: seo?.metaDescription,
    url: `${WEBSITE}/list/${slug}`,
    hasPart: detailedReviewItems.reduce((acc, item) => {
      // Initialize an empty array for non-overall reviews
      const nonOverallReviews = []

      item.ratings.data.forEach(rating => {
        const ratingAttribute =
          rating.attributes.ratingAttribute.data.attributes
        if (ratingAttribute.name !== 'Overall') {
          nonOverallReviews.push({
            '@type': 'Review',
            datePublished: item.publishedAt,
            itemReviewed: {
              '@type': 'Thing',
              name: item.title,
            },
            reviewBody: stripHtml(rating.attributes.content),
            author: {
              '@type': 'Person',
              name: author?.data?.attributes.fullName,
            },
            reviewRating: {
              '@type': 'Rating',
              reviewAspect: ratingAttribute.name,
              ratingValue: rating.attributes.score,
              bestRating: 10,
              worstRating: 1,
            },
          })
        }
      })
      const overallRating = item.ratings.data.find(
        rating =>
          rating.attributes.ratingAttribute.data.attributes.name === 'Overall'
      )
      if (overallRating) {
        acc.push({
          '@type': 'AggregateRating',
          itemReviewed: {
            '@type': 'Thing',
            name: item.title,
          },
          ratingValue: overallRating.attributes.score,
          bestRating: 10,
          worstRating: 1,
          reviewCount: nonOverallReviews.length.toString(), // Adjust this based on your data, if necessary
        })
      }
      return acc.concat(nonOverallReviews)
    }, []),
  }

  return (
    <BaseLayout
      scrollInContent
      seo={{
        title: seo?.metaTitle || title,
        description: seo?.metaDescription,
        canonical: `${WEBSITE}/list/${slug}`,
        jsonLd: reviewListStructuredData,
      }}
    >
      {children}
    </BaseLayout>
  )
}

ListDetails.getLayout = ListDetailsLayout
